import { computed, reactive, ref, watch } from "vue";
import { applyParsedAddress, parseAddressQuery } from "../domain/address-utils";
import { pinSuggestionPoint, pointForFields } from "../domain/suggestion-point";
import { useAddressFormatter } from "@/shared/composables/useAddressFormatter";
import { useWorkspaceCountry } from "@/shared/composables/useWorkspaceCountry";
import { defaultAddressCountry } from "@/shared/utils/addressFieldConfig";
import { useAddressCountryOptions } from "./useAddressCountryOptions";
import { useAddressSearch } from "./useAddressSearch";
function hydrateFromEntity(details, address) {
    details.line1 = address.line1 ?? "";
    details.line2 = address.line2 ?? "";
    details.line3 = address.line3 ?? "";
    details.houseNumber = address.houseNumber ?? "";
    details.houseNumberSuffix = address.houseNumberSuffix ?? "";
    details.postalCode = address.postalCode ?? "";
    details.city = address.city ?? "";
    details.region = address.region ?? "";
    details.country = address.country ?? "";
}
/**
 * Address autocomplete + "add more details" modal + BAN search for a FRESH address with no
 * backing entity — the outer field is a plain display string (`address`).
 */
export function useAddressForm(minChars = 3) {
    const { suggestions: addressSuggestions, loading: addressLoading, search: runAddressSearch, clear, } = useAddressSearch(minChars);
    const workspaceCountry = useWorkspaceCountry();
    const addressFormatter = useAddressFormatter();
    const address = ref("");
    const city = ref("");
    const postalCode = ref("");
    const addressDirty = ref(false);
    const addressDetailsSaved = ref(false);
    const showAddressDetailsModal = ref(false);
    const addressDetails = reactive({
        line1: "",
        line2: "",
        line3: "",
        houseNumber: "",
        houseNumberSuffix: "",
        postalCode: "",
        city: "",
        region: "",
        country: defaultAddressCountry(workspaceCountry.value),
    });
    // A create modal is mounted closed with its list page, usually before the workspace country has
    // been read: the default follows it until a country is set on purpose (typed in the address,
    // details saved, SIRENE) — comparing the value is not enough, a typed "France" equals the FR
    // default.
    let countryIsDefault = true;
    watch(workspaceCountry, (country, previous) => {
        if (countryIsDefault && addressDetails.country === defaultAddressCountry(previous)) {
            addressDetails.country = defaultAddressCountry(country);
        }
    });
    const countryOptions = useAddressCountryOptions();
    function onAddressSearch(query) {
        // The AutoComplete v-model only reflects a *selected* option, while @search fires on
        // every keystroke — write the free-typed text back to `address` so manual entry (an
        // address BAN can't resolve) still saves.
        address.value = query;
        runAddressSearch(query);
    }
    function onAddressSelect(option) {
        const metaCity = option.meta?.city ?? "";
        const metaPostalCode = option.meta?.postalCode ?? "";
        // BAN label already contains "street postalCode city" — extract only the street so we can
        // recompose in the same comma-separated format as saveAddressDetails without duplicating
        // the locality. Locality-only suggestions (no parseable line1) produce just "postalCode city".
        const parsed = parseAddressQuery(option.value);
        const street = parsed.postalCode ? (parsed.line1 ?? "") : option.value;
        const localityPart = [metaPostalCode, metaCity].filter(Boolean).join(" ");
        const parts = [street, localityPart].filter(Boolean);
        address.value = parts.join(", ");
        city.value = metaCity;
        postalCode.value = metaPostalCode;
        addressDetailsSaved.value = false;
        addressDirty.value = true;
    }
    function composeAddressString(details) {
        return addressFormatter.format(details);
    }
    function openAddressDetailsModal() {
        // Re-parsing a string the details modal itself composed would mangle
        // non-French shapes (an NL postal code parses as "1217" + city "KD ..."),
        // so the saved structured values stay authoritative until the user edits
        // the one-line text.
        const composed = composeAddressString(addressDetails);
        if (!(addressDetailsSaved.value && address.value === composed)) {
            if (parseAddressQuery(address.value, addressFormatter.names).country)
                countryIsDefault = false;
            applyParsedAddress(address.value, addressDetails, workspaceCountry.value, addressDetails.country || defaultAddressCountry(workspaceCountry.value), addressFormatter.names);
        }
        showAddressDetailsModal.value = true;
    }
    function saveAddressDetails(details) {
        const composed = composeAddressString(details);
        addressSuggestions.value = [{ value: composed, label: composed }];
        address.value = composed;
        city.value = details.city;
        postalCode.value = details.postalCode;
        Object.assign(addressDetails, details);
        countryIsDefault = false;
        addressDetailsSaved.value = true;
        addressDirty.value = true;
        showAddressDetailsModal.value = false;
    }
    function setFromSirene(newAddress, newCity, newPostalCode) {
        address.value = newAddress;
        city.value = newCity;
        postalCode.value = newPostalCode;
        // SIRENE is the French company registry: its address is French whatever the
        // workspace, so it must not inherit the workspace default country.
        addressDetails.country = defaultAddressCountry("FR");
        countryIsDefault = false;
        if (newAddress) {
            addressSuggestions.value = [{ value: newAddress, label: newAddress }];
        }
        addressDirty.value = false;
        addressDetailsSaved.value = false;
    }
    function reset() {
        clear();
        address.value = "";
        city.value = "";
        postalCode.value = "";
        addressDirty.value = false;
        showAddressDetailsModal.value = false;
        addressDetailsSaved.value = false;
        addressDetails.country = defaultAddressCountry(workspaceCountry.value);
        countryIsDefault = true;
    }
    return {
        address,
        city,
        postalCode,
        addressDirty,
        addressSuggestions,
        addressLoading,
        showAddressDetailsModal,
        addressDetailsSaved,
        addressDetails,
        countryOptions,
        onAddressSearch,
        onAddressSelect,
        openAddressDetailsModal,
        saveAddressDetails,
        setFromSirene,
        reset,
    };
}
/**
 * Address autocomplete + "add more details" modal + BAN search for an address that mirrors an
 * existing `OrganisationAddress` — the outer field is a structured, entity-backed representation
 * (`addressDetails`/`currentAddressQuery`), and `isAddressDirty` tracks edits against it.
 */
export function useAddressFormHydrated(getAddress, minChars = 3) {
    const { suggestions: addressSuggestions, loading: addressLoading, search: runAddressSearch, } = useAddressSearch(minChars);
    const workspaceCountry = useWorkspaceCountry();
    const addressFormatter = useAddressFormatter();
    const toDisplayString = (address) => addressFormatter.format(address);
    const addressValue = computed(() => getAddress());
    const currentAddressQuery = ref(addressValue.value ? toDisplayString(addressValue.value) : "");
    const isAddressDirty = ref(false);
    // skipNextHydrate prevents the store-update watcher from overwriting addressDetails
    // immediately after a save — the form state should stay exactly as the user left it.
    let skipNextHydrate = false;
    // The query addressDetails was last resolved from: a picked suggestion's label
    // must not be re-parsed, or the API meta (city, postal code) is lost.
    let resolvedQuery = null;
    const pinnedPoint = ref(null);
    const showAddressDetailsModal = ref(false);
    const emptyDetails = () => ({
        line1: "",
        line2: "",
        line3: "",
        houseNumber: "",
        houseNumberSuffix: "",
        postalCode: "",
        city: "",
        region: "",
        country: "",
    });
    const addressDetails = reactive(emptyDetails());
    const countryOptions = useAddressCountryOptions();
    function resetStructuredFields() {
        addressDetails.houseNumber = "";
        addressDetails.houseNumberSuffix = "";
        addressDetails.region = "";
    }
    watch(addressValue, (val) => {
        if (val) {
            if (!skipNextHydrate) {
                hydrateFromEntity(addressDetails, val);
                resolvedQuery = null;
                pinnedPoint.value = null;
            }
            skipNextHydrate = false;
            currentAddressQuery.value = toDisplayString(val);
            isAddressDirty.value = false;
        }
        else {
            Object.assign(addressDetails, emptyDetails());
            currentAddressQuery.value = "";
            isAddressDirty.value = false;
            pinnedPoint.value = null;
        }
    }, { immediate: true });
    watch(currentAddressQuery, (query) => {
        const storedDisplay = addressValue.value ? toDisplayString(addressValue.value) : "";
        isAddressDirty.value = query !== storedDisplay;
    });
    function onAddressSearch(query) {
        currentAddressQuery.value = query;
        runAddressSearch(query);
    }
    function parseQueryIntoDetails() {
        const savedCountry = addressDetails.country;
        resetStructuredFields();
        applyParsedAddress(currentAddressQuery.value, addressDetails, workspaceCountry.value, savedCountry || defaultAddressCountry(workspaceCountry.value), addressFormatter.names);
        resolvedQuery = currentAddressQuery.value;
    }
    // Typing only updates currentAddressQuery: a save of the outer field must
    // resolve the typed text first, since addressDetails is what gets persisted.
    function syncAddressDetailsFromQuery() {
        if (isAddressDirty.value && currentAddressQuery.value !== resolvedQuery) {
            parseQueryIntoDetails();
        }
    }
    function onAddressSelect(option) {
        currentAddressQuery.value = option.label;
        resolvedQuery = option.label;
        resetStructuredFields();
        applyParsedAddress(option.label, addressDetails, workspaceCountry.value, option.meta?.country ?? addressDetails.country, addressFormatter.names);
        if (option.meta?.city)
            addressDetails.city = option.meta.city;
        if (option.meta?.postalCode)
            addressDetails.postalCode = option.meta.postalCode;
        pinnedPoint.value = pinSuggestionPoint(option.meta, addressDetails);
    }
    // The picked suggestion's point while addressDetails still holds the address it filled.
    function pointToSave() {
        return pointForFields(pinnedPoint.value, addressDetails);
    }
    function openAddressDetailsModal() {
        if (isAddressDirty.value) {
            // User typed a free-text address — parse it into addressDetails now so the
            // modal reflects what they typed, not the stale entity data.
            parseQueryIntoDetails();
        }
        showAddressDetailsModal.value = true;
    }
    function onBeforeAddressSave() {
        skipNextHydrate = true;
    }
    return {
        addressDetails,
        isAddressDirty,
        currentAddressQuery,
        addressSuggestions,
        addressLoading,
        showAddressDetailsModal,
        countryOptions,
        onAddressSearch,
        onAddressSelect,
        openAddressDetailsModal,
        syncAddressDetailsFromQuery,
        onBeforeAddressSave,
        pointToSave,
    };
}
